import { useEffect, useState } from "react";
import { useAppSelector } from "../app/hooks";
import { getNFitScoreHistory, getScanResultById } from "../services/resource-server/resource-server";
import { convertDateForHistory } from "../utilities/display-formats";

const EXCLUDED_KEYS = new Set(["nFit", "DIR_anisotropy", "DIR_asymmetry", "DIR_offset", "SPD_offset"]);
const ALL_TIME = new Date('1900-01-01').toISOString();

export interface CobraDelta {
    deltas: Record<string, number>;
    primaryDate: string;
    previousDate: string;
}

/*
 * Per-metric delta (current selected scan minus the scan immediately
 * before it) for the COBRA metrics, along with both scans' dates, or
 * null when there is no selected scan or no earlier scan to compare
 * against. Fetches its own scan history rather than relying on another
 * page's chart having populated it, so it works regardless of which
 * page loads first.
 */
export function useCobraDelta(): CobraDelta | null {
    const selectedScan = useAppSelector((state) => state.selectedScan.record);
    const [result, setResult] = useState<CobraDelta | null>(null);

    useEffect(() => {
        if (selectedScan === null) {
            setResult(null);
            return;
        }

        let cancelled = false;
        const fetchDelta = async () => {
            const history = await getNFitScoreHistory(ALL_TIME);
            const index = history.findIndex(record => record.id === selectedScan.id);
            if (index <= 0) {
                if (!cancelled) setResult(null);
                return;
            }

            const [primaryStat, previousStat] = await Promise.all([
                getScanResultById(history[index].id),
                getScanResultById(history[index - 1].id),
            ]);

            const deltas: Record<string, number> = {};
            for (const key in primaryStat) {
                if (EXCLUDED_KEYS.has(key)) continue;

                const rawValue = primaryStat[key as keyof typeof primaryStat];
                const prevRawValue = previousStat[key as keyof typeof previousStat];
                if (typeof rawValue === "string" || typeof prevRawValue === "string" || prevRawValue === undefined) continue;

                deltas[key] = +rawValue - +prevRawValue;
            }

            if (!cancelled) {
                setResult({
                    deltas,
                    primaryDate: convertDateForHistory(history[index].date),
                    previousDate: convertDateForHistory(history[index - 1].date),
                });
            }
        };
        fetchDelta();

        return () => { cancelled = true; };
    }, [selectedScan]);

    return result;
}
